import React from 'react';
import { useLanguage } from '../i18n';

export default function PrivacyPage() {
  const { t } = useLanguage();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '2rem 1rem' }}>
      <div style={{ maxWidth: '48rem', margin: '0 auto', backgroundColor: '#FFFFFF', borderRadius: '1rem', padding: '3rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: '800', color: '#1A1A1A', marginBottom: '0.5rem' }}>Privacy Policy</h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Last updated: March 2026 · Veyl</p>
        </div>

        {/* Translation Notice */}
        <div style={{
          backgroundColor: 'rgba(27,122,138,0.08)',
          border: '1px solid #1B7A8A',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '2rem',
          fontSize: '0.875rem',
          color: '#1B7A8A'
        }}>
          🌐 {t('legalEnglishOnly')}
        </div>

        {[
          {
            title: '1. Introduction',
            content: `Veyl ("we", "our", "the service") is committed to protecting your privacy. This policy explains what data we collect, how we use it, and your rights regarding your data. Veyl is operated from Kenya and is designed with anonymity as a core principle. By using Veyl, you agree to the collection and use of information as described here.`
          },
          {
            title: '2. Data We Collect',
            content: `We collect your email address (for authentication only), a pseudonym/nickname you choose, your timezone, goal category, goal description, and match preference. We also store your messages, voice notes, check-in responses, group chat activity, community chat activity, notifications, and avatar selection. We do not collect your real name, phone number, physical address, or any government-issued identification.`
          },
          {
            title: '3. How We Use Your Data',
            content: `We use your data to match you with accountability partners based on goal category, timezone, and match preference; facilitate anonymous communication between matched partners; track your goal progress and check-ins; send in-app notifications relevant to your activity; provide and improve the Veyl service; and enforce our community safety policies. We do not sell your data to third parties. We do not use your data for advertising purposes.`
          },
          {
            title: '4. Anonymity',
            content: `Veyl is designed to be anonymous by default. Your accountability partner sees only your chosen pseudonym, avatar, goal category, and the content you voluntarily share in chat and check-ins. Your email address is never visible to other users. Daily notes in check-ins are private and visible only to you — partners can only see your check-in status and weekly reflections. We strongly encourage you not to share personally identifying information with your partner through the platform.`
          },
          {
            title: '5. Voice Notes',
            content: `Voice notes are stored as part of your chat data within Firebase. Voice notes include an optional pitch modulation feature to further protect your vocal anonymity. Voice notes are subject to storage limits and may be removed if they exceed those limits. We do not use voice notes for any purpose other than delivering them to your intended recipient.`
          },
          {
            title: '6. Data Storage',
            content: `Your data is stored securely using Google Firebase, which uses industry-standard encryption at rest and in transit. Firebase infrastructure may be located outside of Kenya. By using Veyl, you consent to your data being stored on Firebase infrastructure. We implement Firestore Security Rules to ensure users can only access data they are authorised to view.`
          },
          {
            title: '7. Payment Data',
            content: `Premium subscriptions are processed through Polar, our third-party payment provider. We do not store your payment card details. Polar handles all payment data in accordance with their own privacy policy and PCI-DSS compliance standards. We only receive confirmation of successful payment and your subscription status.`
          },
          {
            title: '8. Data Retention',
            content: `We retain your data for as long as your account is active. When you delete your account through the Settings page, we permanently delete your user profile, Veyls, check-ins, notifications, and all associated data within 24 hours. Chat messages and voice notes are also permanently removed. We do not retain backups of deleted accounts.`
          },
          {
            title: '9. Your Rights',
            content: `You have the right to access the data we hold about you, correct inaccurate data, request deletion of your data, and withdraw consent at any time. To exercise these rights, use the Delete Account feature in Settings or contact us at support@veyl.space. We will respond to all data requests within 30 days.`
          },
          {
            title: '10. Age Requirement',
            content: `Veyl is only available to users aged 18 and above. We enforce age verification during onboarding, and by creating an account you confirm that you are at least 18 years old. We do not knowingly collect data from anyone under 18. If we become aware that a user under 18 has provided us with personal data, we will delete their account and associated data immediately. If you believe a user under 18 has created an account, please contact us at support@veyl.space.`
          },
          {
            title: '11. Third Party Services',
            content: `We use the following third-party services: Google Firebase for authentication, database, and hosting; Polar for payment processing of Premium subscriptions. These services have their own privacy policies which govern their use of your data. We do not share your data with any other third parties beyond what is necessary to operate the service.`
          },
          {
            title: '12. Security',
            content: `We implement Firestore Security Rules to ensure users can only access their own data and data they are explicitly permitted to view. All data is transmitted over HTTPS. However, no method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security and encourage you to use a strong password and keep your account credentials private.`
          },
          {
            title: '13. Cookies & Tracking',
            content: `Veyl does not use advertising cookies or third-party tracking. Firebase may use session tokens and local storage to maintain your authenticated session. We do not use any analytics services that track your behaviour across other websites.`
          },
          {
            title: '14. Changes to This Policy',
            content: `We may update this Privacy Policy from time to time. We will notify users of significant changes through the app or via email. The date at the top of this page reflects when the policy was last updated. Continued use of Veyl after changes constitutes acceptance of the updated policy.`
          },
          {
            title: '15. Governing Law',
            content: `This Privacy Policy is governed by the laws of Kenya, including the Kenya Data Protection Act 2019. If you have unresolved concerns, you may contact the Office of the Data Protection Commissioner of Kenya.`
          },
          {
            title: '16. Contact',
            content: `For any privacy-related questions, requests, or concerns, contact us at support@veyl.space. We take privacy seriously and will respond to all enquiries within 30 days.`
          },
        ].map(section => (
          <div key={section.title} style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1A1A1A', marginBottom: '0.75rem' }}>
              {section.title}
            </h2>
            <p style={{ color: '#4b5563', lineHeight: '1.8', fontSize: '0.95rem' }}>
              {section.content}
            </p>
          </div>
        ))}

        <div style={{ marginTop: '3rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
          <a href="/" style={{ color: '#1B7A8A', textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem' }}>← Back to Veyl</a>
        </div>
      </div>
    </div>
  );
}